import {
  ReactElement,
  SyntheticEvent,
  HTMLAttributes,
  useCallback,
  memo,
} from 'react';
import { AutocompleteValue } from '@mui/material';
import { useFormikContext } from 'formik';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { TemplateRoute } from '~services/templateRoute/types';
import { convertDriverUserToDriverCompanyBase } from '~utils/driver';
import AccessFeature from '~components/AccessFeature';

import { useAssistantGenerateRoutesDialogByTemplateContext } from '../../../../Context';
import {
  FORM_BY_TEMPLATE_FIELDS_NAME,
  getFormByTemplateInitialValues,
} from '../../../../utils';

const AssistantGenerateRoutesDialogByTemplateContentFormTemplateAutocomplete =
  (): ReactElement => {
    const {
      data: {
        templates,
        selectedTemplate: {
          value: defaultSelectedTemplate,
          setValue: setSelectedTemplate,
        },
        selectedDriver: { setValue: setSelectedDriver },
      },
    } = useAssistantGenerateRoutesDialogByTemplateContext();

    const { setValues } =
      useFormikContext<ReturnType<typeof getFormByTemplateInitialValues>>();

    const onChange = useCallback(
      async (
        _event: SyntheticEvent,
        newDriver: AutocompleteValue<TemplateRoute, false, false, false>,
      ) => {
        setSelectedTemplate(null);
        setSelectedDriver(null);

        const initialValuesForm = getFormByTemplateInitialValues();

        if (!newDriver) {
          setValues(initialValuesForm, true);
          return;
        }

        setValues({
          ...initialValuesForm,
          [FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_NAME]:
            newDriver.apiTransaction.name,
        });

        setTimeout(() => {
          setSelectedTemplate(newDriver);

          const selectedDriver = newDriver.driver
            ? convertDriverUserToDriverCompanyBase(newDriver.driver)
            : null;

          setSelectedDriver(selectedDriver);
        }, 250);
      },
      [setSelectedTemplate, setValues, setSelectedDriver],
    );

    const isOptionEqualToValue = useCallback(
      (option: TemplateRoute, value: TemplateRoute) =>
        option.routeId === value.routeId,
      [],
    );

    const getOptionLabel = useCallback(
      (option: TemplateRoute) =>
        `${
          option.apiTransaction.name || 'Sin Nombre'
        } (${option.routeCode.toUpperCase()})`,
      [],
    );

    const renderOption = useCallback(
      (
        optionProps: HTMLAttributes<HTMLLIElement>,
        option: TemplateRoute,
      ): JSX.Element => (
        <li {...optionProps} key={option.routeId}>
          {getOptionLabel(option)}
        </li>
      ),
      [getOptionLabel],
    );

    return (
      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.MgmRepeatRoutes}
        defaultValue={defaultSelectedTemplate}
        options={templates}
        label="Seleccione una plantilla"
        onChange={onChange}
        includeInputInList={false}
        filterSelectedOptions
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
      />
    );
  };

export default memo(
  AssistantGenerateRoutesDialogByTemplateContentFormTemplateAutocomplete,
);
